import { supabase } from '@/app/lib/supabase';
import { degreePlansApi, schedulesApi } from '@/app/services/api';
import { applyDegreePlanOperations, applyScheduleOperations, summarizeScheduleOperations } from '@/app/lib/academicChangeSet';
import type {
  DegreePlanChangeOperation,
  DegreePlanRevision,
  DegreePlanSnapshot,
  ScheduleChangeOperation,
  ScheduleChangeProposal,
  ScheduleRevision,
  ScheduleSnapshot,
} from '@/app/types/academicChangeSet';

function unwrapRpcJson<T>(value: unknown): T {
  if (Array.isArray(value)) return value[0] as T;
  return value as T;
}

export async function getScheduleRevisions(scheduleId: string, limit = 20): Promise<ScheduleRevision[]> {
  const { data, error } = await supabase
    .from('schedule_revisions')
    .select('*')
    .eq('schedule_id', scheduleId)
    .order('sequence', { ascending: false })
    .limit(Math.min(Math.max(limit, 1), 50));
  if (error) throw error;
  return (data || []) as ScheduleRevision[];
}

export async function getDegreePlanRevisions(planId: number, limit = 20): Promise<DegreePlanRevision[]> {
  const { data, error } = await supabase
    .from('degree_plan_revisions')
    .select('*')
    .eq('degree_plan_id', planId)
    .order('sequence', { ascending: false })
    .limit(Math.min(Math.max(limit, 1), 50));
  if (error) throw error;
  return (data || []) as DegreePlanRevision[];
}

export async function getPendingScheduleProposals(scheduleId: string): Promise<ScheduleChangeProposal[]> {
  const { data, error } = await supabase
    .from('schedule_change_proposals')
    .select('*')
    .eq('schedule_id', scheduleId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(10);
  if (error) throw error;
  return (data || []) as ScheduleChangeProposal[];
}

export async function createScheduleChangeProposal(input: {
  scheduleId: string;
  operations: ScheduleChangeOperation[];
  summary?: string;
  validation?: Record<string, unknown>;
  conversationId?: string | null;
  baseRevisionId?: string | null;
  idempotencyKey?: string;
}): Promise<ScheduleChangeProposal> {
  const schedule = await schedulesApi.get(input.scheduleId);
  if (!schedule) throw new Error('Schedule not found');
  const snapshot: ScheduleSnapshot = {
    name: schedule.name || 'Schedule',
    term_id: schedule.term_id ?? null,
    description: schedule.description ?? null,
    is_public: Boolean(schedule.is_public),
    classes: Array.isArray(schedule.classes) ? schedule.classes : [],
    configuration: schedule.configuration && typeof schedule.configuration === 'object' ? schedule.configuration : null,
  };
  const preview = applyScheduleOperations(snapshot, input.operations);
  const { data, error } = await supabase.rpc('create_schedule_change_proposal', {
    p_schedule_id: input.scheduleId,
    p_operations: preview.operations,
    p_preview_snapshot: preview.snapshot,
    p_summary: input.summary?.trim() || summarizeScheduleOperations(preview.operations),
    p_validation: input.validation || {},
    p_conversation_id: input.conversationId || null,
    p_base_revision_id: input.baseRevisionId || null,
    ...(input.idempotencyKey ? { p_idempotency_key: input.idempotencyKey } : {}),
  });
  if (error) throw error;
  const proposal = unwrapRpcJson<ScheduleChangeProposal>(data);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('assistant:schedule-proposal-created', { detail: { scheduleId: input.scheduleId, proposalId: proposal.id } }));
  }
  return proposal;
}

export async function createDegreePlanChangeProposal(input: {
  planId: number;
  operations: DegreePlanChangeOperation[];
  summary: string;
  validation?: Record<string, unknown>;
  conversationId?: string | null;
  baseRevisionId?: string | null;
  idempotencyKey?: string;
}): Promise<Record<string, unknown>> {
  const record = await degreePlansApi.get(input.planId);
  if (!record) throw new Error('Degree plan not found');
  const snapshot: DegreePlanSnapshot = {
    name: record.name || record.plan?.name || 'Degree Plan',
    plan: record.plan || {},
    is_shareable: Boolean(record.is_shareable),
  };
  const preview = applyDegreePlanOperations(snapshot, input.operations);
  const { data, error } = await supabase.rpc('create_degree_plan_change_proposal', {
    p_degree_plan_id: input.planId,
    p_operations: input.operations,
    p_preview_snapshot: preview,
    p_summary: input.summary,
    p_validation: input.validation || {},
    p_conversation_id: input.conversationId || null,
    p_base_revision_id: input.baseRevisionId || null,
    ...(input.idempotencyKey ? { p_idempotency_key: input.idempotencyKey } : {}),
  });
  if (error) throw error;
  return unwrapRpcJson<Record<string, unknown>>(data);
}

export async function applyScheduleChangeProposal(proposalId: string) {
  const { data, error } = await supabase.rpc('apply_schedule_change_proposal', { p_proposal_id: proposalId });
  if (error) throw error;
  const result = unwrapRpcJson<Record<string, unknown>>(data);
  if (typeof window !== 'undefined' && result.status === 'applied') {
    window.dispatchEvent(new CustomEvent('assistant:schedule-updated', { detail: { scheduleId: result.scheduleId } }));
    window.dispatchEvent(new CustomEvent('assistant:schedule-proposal-resolved', { detail: result }));
  }
  return result;
}

export async function dismissScheduleChangeProposal(proposalId: string) {
  const { data, error } = await supabase.rpc('dismiss_schedule_change_proposal', { p_proposal_id: proposalId });
  if (error) throw error;
  const result = unwrapRpcJson<Record<string, unknown>>(data);
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('assistant:schedule-proposal-resolved', { detail: result }));
  return result;
}

export async function applyDegreePlanChangeProposal(proposalId: string) {
  const { data, error } = await supabase.rpc('apply_degree_plan_change_proposal', { p_proposal_id: proposalId });
  if (error) throw error;
  const result = unwrapRpcJson<Record<string, unknown>>(data);
  if (typeof window !== 'undefined' && result.status === 'applied') {
    window.dispatchEvent(new CustomEvent('assistant:degree-plan-updated', { detail: { planId: result.degreePlanId } }));
  }
  return result;
}

export async function dismissDegreePlanChangeProposal(proposalId: string) {
  const { data, error } = await supabase.rpc('dismiss_degree_plan_change_proposal', { p_proposal_id: proposalId });
  if (error) throw error;
  return unwrapRpcJson<Record<string, unknown>>(data);
}

export async function restoreScheduleRevision(revisionId: string) {
  const { data, error } = await supabase.rpc('restore_schedule_revision', { p_revision_id: revisionId });
  if (error) throw error;
  const result = unwrapRpcJson<Record<string, unknown>>(data);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('assistant:schedule-updated', { detail: { scheduleId: result.scheduleId } }));
    window.dispatchEvent(new CustomEvent('assistant:schedule-proposal-resolved', { detail: result }));
  }
  return result;
}

export async function restoreDegreePlanRevision(revisionId: string) {
  const { data, error } = await supabase.rpc('restore_degree_plan_revision', { p_revision_id: revisionId });
  if (error) throw error;
  const result = unwrapRpcJson<Record<string, unknown>>(data);
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('assistant:degree-plan-updated', { detail: { planId: result.degreePlanId } }));
  return result;
}

export function compareScheduleSnapshots(from: ScheduleSnapshot, to: ScheduleSnapshot) {
  const previous = new Set(from.classes);
  const next = new Set(to.classes);
  return {
    addedSectionIds: to.classes.filter((sectionId) => !previous.has(sectionId)),
    removedSectionIds: from.classes.filter((sectionId) => !next.has(sectionId)),
    visibilityChanged: from.is_public !== to.is_public,
    nameChanged: from.name !== to.name,
  };
}
